import { locationProgramVariationSchema } from "../LocationFormSections/schemas/locationProgramVariationSchema";

/**
 * KB-4174 — the keys the program modal owns: everything its schema declares (zod strips the rest on
 * save), plus the UI-only `colorScheme` the modal deliberately drops. A key the form owns follows the
 * form exactly, including being cleared; a key it does not own (e.g. KB-4169's
 * `placeholderDiscoveryUntil`) is the stored variation's, and an edit must not erase it.
 */
const FORM_OWNED_VARIATION_KEYS: ReadonlySet<string> = new Set([
  ...Object.keys(locationProgramVariationSchema.shape),
  "colorScheme",
]);

export function isFormOwnedVariationKey(key: string): boolean {
  return FORM_OWNED_VARIATION_KEYS.has(key);
}

/** The edited variation, plus every stored field the form does not own. */
export function keepFieldsTheFormDoesNotOwn<T extends object>(
  stored: Record<string, unknown> | null | undefined,
  edited: T,
): T {
  if (!stored || typeof stored !== "object") return edited;
  const kept: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(stored)) {
    if (!isFormOwnedVariationKey(k)) kept[k] = v;
  }
  return { ...kept, ...edited };
}

/**
 * JSON-mode input the save will NOT apply: keys the form does not own whose value differs from the
 * stored variation's. zod strips them (an edit keeps the stored value), so the operator must be told.
 */
export function unappliedJsonVariationKeys(
  json: Record<string, unknown> | null | undefined,
  stored: Record<string, unknown> | null | undefined,
): string[] {
  if (!json || typeof json !== "object") return [];
  return Object.keys(json).filter(
    (k) => !isFormOwnedVariationKey(k) && JSON.stringify(json[k]) !== JSON.stringify(stored?.[k]),
  );
}
